import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../../seo/useSEO';
import { t } from '../../i18n/pt';
import { api, extractError } from '../../lib/api';
import { Button } from '../../components/ui/Button';
import { Field } from '../../components/ui/Field';
import { useFormErrors } from '../../hooks/useFormErrors';
import { combine, email as emailRule, required } from '../../lib/validate';
import styles from './Auth.module.scss';

export default function ForgotPasswordPage(): JSX.Element {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { errors, setErrors, clearError } = useFormErrors<'email'>();

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const emailErr = combine(required, emailRule)(email);
    if (emailErr) {
      setErrors({ email: emailErr });
      return;
    }
    setErrors({});
    setSubmitting(true);
    setError(null);
    try {
      await api.post('/auth/forgot-password', { email });
      setSuccess(true);
    } catch (err) {
      setError(extractError(err, 'Falha ao enviar email'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO title={t.auth.forgotTitle} noindex />
      <div className={styles.wrapper}>
        <div className={styles.card}>
          <header className={styles.intro}>
            <h1 className={styles.title}>{t.auth.forgotTitle}</h1>
            <p className={styles.lead}>
              Indica o teu email e enviamos-te um link para definires uma nova palavra-passe.
            </p>
          </header>

          {success ? (
            <p className={styles.success}>
              Se existir uma conta com este email, vais receber as instruções em breve.
            </p>
          ) : (
            <form className={styles.form} onSubmit={handleSubmit} noValidate>
              <Field
                label={t.auth.email}
                required
                error={errors.email}
                className={styles.field}
              >
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (errors.email) clearError('email');
                  }}
                  autoComplete="email"
                />
              </Field>
              {error ? <p className={styles.error}>{error}</p> : null}
              <Button type="submit" variant="primary" size="lg" fullWidth disabled={submitting}>
                {submitting ? t.common.loading : t.auth.forgotCta}
              </Button>
            </form>
          )}

          <div className={styles.links}>
            <Link to="/conta/login">Voltar para iniciar sessão</Link>
          </div>
        </div>
      </div>
    </>
  );
}
